import { Injectable, signal, computed } from '@angular/core';
import { Router } from '@angular/router';
import { supabase } from '../../../lib/supabase';
import { User, mapSupabaseUser } from '../models/user.model';
import { AuthError } from '@supabase/supabase-js';
import { environment } from '../../../environments/environment';

export interface AuthResponse {
  success: boolean;
  error?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  // Signals für reaktiven State
  currentUser = signal<User | null>(null);
  loading = signal<boolean>(false);
  isAuthenticated = computed(() => !!this.currentUser());

  // Track if we've completed initial session check
  private initialSessionChecked = false;

  constructor(private router: Router) {
    // Session beim Start prüfen
    this.checkSession().then(() => {
      this.initialSessionChecked = true;
    });

    // Auth State Listener
    supabase.auth.onAuthStateChange((event, session) => {
      const currentUrl = this.router.url;
      const isPublicRoute = currentUrl.startsWith('/f/');

      this.currentUser.set(mapSupabaseUser(session?.user || null));

      // NEVER redirect if on a public route
      if (isPublicRoute) {
        return;
      }

      const isAuthRoute =
        currentUrl === '/login' ||
        currentUrl.startsWith('/accept-invite') ||
        currentUrl === '/verify-email' ||
        currentUrl === '/reset-password';

      const isProtectedAppRoute =
        currentUrl === '/dashboard' ||
        currentUrl.startsWith('/admin') ||
        currentUrl.startsWith('/finder/');

      // Don't navigate on INITIAL_SESSION or TOKEN_REFRESHED - these fire automatically
      if (event === 'INITIAL_SESSION' || event === 'TOKEN_REFRESHED') {
        return;
      }

      // For SIGNED_IN event: only navigate if it's a "real" sign-in (not initial session)
      // If initialSessionChecked is false, this is likely the initial load with existing session
      if (event === 'SIGNED_IN') {
        // If we haven't checked initial session yet, this is likely an initial load
        // Don't redirect - let the router handle navigation naturally
        if (!this.initialSessionChecked) {
          return;
        }

        // If user is on login page, redirect after successful login
        if (currentUrl === '/login' || currentUrl.startsWith('/login')) {
          const urlTree = this.router.parseUrl(currentUrl);
          const returnUrl = this.sanitizeReturnUrl(urlTree.queryParams['returnUrl']);
          this.router.navigateByUrl(returnUrl);
          return;
        }

        // Only navigate to dashboard if not already on a protected route, public route, or auth route
        // Also don't redirect if on root path - let router handle it
        if (currentUrl !== '/' && !isProtectedAppRoute && !isAuthRoute) {
          this.router.navigate(['/dashboard']);
        }
      } else if (event === 'SIGNED_OUT') {
        // Don't redirect if already on an auth route or public route
        if (!isAuthRoute && !isPublicRoute) {
          this.router.navigate(['/login']);
        }
      }
    });
  }

  /** Only allow in-app paths; blocks open redirects like //evil.com or https://evil.com. */
  private sanitizeReturnUrl(raw: unknown): string {
    if (typeof raw !== 'string') {
      return '/dashboard';
    }
    const url = raw.trim();
    if (!url.startsWith('/') || url.startsWith('//') || url.startsWith('/\\')) {
      return '/dashboard';
    }
    return url;
  }

  /**
   * Prüft ob eine aktive Session existiert
   */
  async checkSession(): Promise<void> {
    try {
      this.loading.set(true);
      const { data } = await supabase.auth.getSession();
      this.currentUser.set(mapSupabaseUser(data.session?.user || null));
    } catch (error) {
      console.error('Session check error:', error);
      this.currentUser.set(null);
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * Registrierung mit Email und Passwort
   * Sendet automatisch Verification Email
   */
  async signUp(email: string, password: string): Promise<AuthResponse> {
    try {
      this.loading.set(true);

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${environment.appUrl}/verify-email`,
        },
      });

      if (error) {
        return { success: false, error: this.translateError(error) };
      }

      if (data.user && !data.user.email_confirmed_at) {
        // User wurde erstellt, muss aber Email verifizieren
        return {
          success: true,
          error: 'Bitte überprüfen Sie Ihre E-Mails zur Verifizierung.',
        };
      }

      return { success: true };
    } catch (error: any) {
      return {
        success: false,
        error: 'Ein unerwarteter Fehler ist aufgetreten.',
      };
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * Login mit Email und Passwort
   */
  async signIn(email: string, password: string): Promise<AuthResponse> {
    try {
      this.loading.set(true);

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        return { success: false, error: this.translateError(error) };
      }

      if (data.user && !data.user.email_confirmed_at) {
        // Email noch nicht verifiziert
        await supabase.auth.signOut();
        return {
          success: false,
          error:
            'Bitte verifizieren Sie zuerst Ihre E-Mail-Adresse. Überprüfen Sie Ihr Postfach.',
        };
      }

      this.currentUser.set(mapSupabaseUser(data.user));
      return { success: true };
    } catch (error: any) {
      return {
        success: false,
        error: 'Ein unerwarteter Fehler ist aufgetreten.',
      };
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * Logout
   */
  async signOut(): Promise<void> {
    try {
      this.loading.set(true);
      await supabase.auth.signOut();
      this.currentUser.set(null);
    } catch (error) {
      console.error('Sign out error:', error);
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * Passwort zurücksetzen - Sendet Reset Email
   */
  async resetPassword(email: string): Promise<AuthResponse> {
    try {
      this.loading.set(true);

      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${environment.appUrl}/reset-password`,
      });

      if (error) {
        return { success: false, error: this.translateError(error) };
      }

      return {
        success: true,
        error:
          'Bitte überprüfen Sie Ihre E-Mails für den Link zum Zurücksetzen des Passworts.',
      };
    } catch (error: any) {
      return {
        success: false,
        error: 'Ein unerwarteter Fehler ist aufgetreten.',
      };
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * Profil nach Einladung aktualisieren (Name + Passwort)
   */
  async completeInviteProfile(
    fullName: string,
    password: string
  ): Promise<AuthResponse> {
    try {
      this.loading.set(true);

      const { error } = await supabase.auth.updateUser({
        password,
        data: { full_name: fullName },
      });

      if (error) {
        return { success: false, error: this.translateError(error) };
      }

      const {
        data: { user },
      } = await supabase.auth.getUser();
      this.currentUser.set(mapSupabaseUser(user));

      return { success: true };
    } catch {
      return {
        success: false,
        error: 'Ein unerwarteter Fehler ist aufgetreten.',
      };
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * Session aus URL-Hash setzen (Einladungslink von Supabase)
   */
  async setSessionFromHash(fragment: string): Promise<AuthResponse> {
    const params = new URLSearchParams(fragment);
    const accessToken = params.get('access_token');
    const refreshToken = params.get('refresh_token');

    if (!accessToken || !refreshToken) {
      return { success: false, error: 'Ungültiger Einladungslink.' };
    }

    try {
      this.loading.set(true);
      const { data, error } = await supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken,
      });

      if (error) {
        return { success: false, error: this.translateError(error) };
      }

      this.currentUser.set(mapSupabaseUser(data.session?.user ?? null));
      return { success: true };
    } catch {
      return {
        success: false,
        error: 'Ein unerwarteter Fehler ist aufgetreten.',
      };
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * Neues Passwort setzen (nach Reset Link)
   */
  async updatePassword(newPassword: string): Promise<AuthResponse> {
    try {
      this.loading.set(true);

      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) {
        return { success: false, error: this.translateError(error) };
      }

      return { success: true };
    } catch (error: any) {
      return {
        success: false,
        error: 'Ein unerwarteter Fehler ist aufgetreten.',
      };
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * Übersetzt Supabase Fehler ins Deutsche
   */
  private translateError(error: AuthError): string {
    const errorMessages: { [key: string]: string } = {
      'Invalid login credentials': 'Ungültige E-Mail oder Passwort.',
      'Email not confirmed': 'E-Mail wurde noch nicht bestätigt.',
      'User already registered': 'Ein Benutzer mit dieser E-Mail existiert bereits.',
      'Password should be at least 6 characters':
        'Das Passwort muss mindestens 6 Zeichen lang sein.',
      'Unable to validate email address: invalid format':
        'Ungültige E-Mail-Adresse.',
      'Email rate limit exceeded': 'Zu viele Anfragen. Bitte versuchen Sie es später erneut.',
      'Invalid email or password': 'Ungültige E-Mail oder Passwort.',
    };

    return errorMessages[error.message] || error.message || 'Ein Fehler ist aufgetreten.';
  }
}
